/**
 * Checkout Form Component
 * Requests card registration with Flow.cl
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckoutSession } from '../types/api';
import ErrorAlert from './ErrorAlert';
import LoadingSpinner from './LoadingSpinner';
import SessionTimer from './SessionTimer';
import { checkoutApi } from '../lib/api';

interface CheckoutFormProps {
  session: CheckoutSession;
  isSubmitting?: boolean;
  onSubmit?: () => Promise<void>;
  error?: string;
}

export default function CheckoutForm({
  session,
  isSubmitting = false,
  error: externalError,
}: CheckoutFormProps) {
  const { t } = useTranslation();
  const [localError, setLocalError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // Helper to parse original_price (can be number or string)
  const getOriginalPrice = (): number | null => {
    const price = session.metadata?.original_price;
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const parsed = parseFloat(price);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const handleProcessPayment = async () => {
    if (isSessionExpired) {
      setLocalError(t('checkout.errors.sessionExpired'));
      return;
    }

    setLocalError(null);
    setIsLoading(true);

    try {
      // Save session ID to sessionStorage so it can be retrieved if Flow redirects
      // without query parameters (e.g., when user presses "Volver")
      sessionStorage.setItem('checkout_session_id', session.session_id);

      // Initiate card registration with Flow
      // Pass full session object to generate session proof for security
      const { registration_url } = await checkoutApi.registerCard(session.session_id, session);

      if (!registration_url) {
        throw new Error(t('checkout.errors.invalidFlowResponse') || 'Invalid response from Flow');
      }

      // Redirect to Flow for secure card registration
      // Use a small delay to ensure error handling is set up
      setTimeout(() => {
        window.location.href = registration_url;
      }, 100);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('checkout.errors.paymentFailed');
      setLocalError(message);
      setIsLoading(false);
    }
  };

  const handleSessionExpired = () => {
    setIsSessionExpired(true);
    setLocalError(t('checkout.errors.sessionExpiredDetails') || 'Por favor, intenta nuevamente desde la página de origen');
  };

  const currency = session.plan_snapshot.currency || 'CLP';
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      minimumFractionDigits: currency === 'CLP' ? 0 : 2,
    }).format(value);

  const formattedPrice = formatCurrency(session.plan_snapshot.price);
  const originalPrice = getOriginalPrice();
  const subscriptionLabel =
    session.plan_snapshot.interval === 'month' ? t('checkout.monthlySubscription') : t('checkout.yearlySubscription');
  const cadenceLabel = session.plan_snapshot.interval === 'month' ? t('checkout.perMonth') : t('checkout.perYear');
  const hasFeatures = Object.keys(session.plan_snapshot.features).length > 0;

  const renderPromotion = () => {
    if (session.plan_snapshot.interval !== 'year' || !session.metadata) {
      return null;
    }

    if (typeof session.metadata.free_months === 'number' && session.metadata.free_months > 0) {
      return (
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/60 bg-emerald-50/80 dark:bg-emerald-500/10 px-4 py-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-200 shadow-inner">
          <svg className="h-4 w-4 text-emerald-500 dark:text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            {session.metadata.free_months === 1
              ? t('checkout.freeMonth')
              : `${t('checkout.freeMonths')} (${session.metadata.free_months})`}
          </span>
        </div>
      );
    }

    if (typeof session.metadata.discount_percentage === 'number' && session.metadata.discount_percentage > 0) {
      return (
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/60 bg-amber-50/80 dark:bg-amber-500/10 px-4 py-1.5 text-sm font-semibold text-amber-700 dark:text-amber-100 shadow-inner">
          <svg className="h-4 w-4 text-amber-500 dark:text-amber-200" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
          <span>
            {t('checkout.discountPercentage')} {session.metadata.discount_percentage}%
          </span>
        </div>
      );
    }

    return null;
  };

  const promotionBadge = renderPromotion();

  if (isLoading || isSubmitting) {
    return <LoadingSpinner message={t('checkout.processing')} />;
  }

  const currentError = externalError || localError;
  const isDisabled = isLoading || isSubmitting || isSessionExpired;

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {currentError && (
        <ErrorAlert message={currentError} onClose={() => setLocalError(null)} />
      )}

      <div className="relative rounded-3xl border border-slate-200/70 dark:border-white/10 bg-white/90 dark:bg-slate-900/70 shadow-[0_30px_80px_rgba(15,23,42,0.55)]">
        <div className="absolute inset-0 rounded-3xl bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25),_transparent)] opacity-60"></div>
        <div className="relative z-10 p-4 sm:p-8 space-y-6 sm:space-y-8">
          {/* Plan Header */}
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {session.plan_snapshot.name}
              </h2>
              <p className="text-sm text-gray-600 dark:text-white/70">{subscriptionLabel}</p>
            </div>

            <div className="text-sm text-gray-500 dark:text-white/70 text-left md:text-right">
              {session.organization_name && (
                <p className="font-semibold text-gray-900 dark:text-white">{session.organization_name}</p>
              )}
              {session.saas && <p className="text-xs text-gray-500 dark:text-white/60">{session.saas}</p>}
            </div>
          </div>

          {/* Price & promotion */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-2xl border border-white/60 dark:border-white/10 bg-white/80 dark:bg-white/5 p-4 shadow-inner">
            <div>
              {originalPrice && originalPrice > session.plan_snapshot.price && (
                <p className="text-sm text-gray-500 dark:text-white/60 line-through">
                  {formatCurrency(originalPrice)}
                </p>
              )}
              <div className="flex items-baseline gap-2">
                <span className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white">
                  {formattedPrice}
                </span>
                <span className="text-sm text-gray-500 dark:text-white/70">{cadenceLabel}</span>
              </div>
            </div>

            {promotionBadge && <div className="flex justify-start sm:justify-end">{promotionBadge}</div>}
          </div>

          {/* Features */}
          {hasFeatures && (
            <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white/80 dark:bg-white/5 p-3 sm:p-5">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('checkout.features')}
              </p>
              <ul className="mt-2 sm:mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {Object.entries(session.plan_snapshot.features).map(([key, value]) => (
                  <li key={key} className="flex items-start gap-2 text-sm text-gray-700 dark:text-white/80">
                    <svg
                      className="h-4 w-4 text-emerald-500 dark:text-emerald-300 mt-1 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{String(value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Payment Section */}
          <div className="border-t border-slate-200/80 dark:border-white/10 pt-6 sm:pt-8 space-y-4 sm:space-y-6">
            <button
              onClick={handleProcessPayment}
              disabled={isDisabled}
              className="btn-primary w-full py-2 sm:py-3 text-sm sm:text-base shadow-lg shadow-primary-500/30"
            >
              {isSessionExpired
                ? t('checkout.errors.sessionExpired')
                : isDisabled
                ? t('checkout.processing')
                : t('checkout.proceedToPayment')}
            </button>

            <div className="info-box info-box-blue space-y-3">
              <SessionTimer expiresAt={session.expires_at} onExpired={handleSessionExpired} />
              <p className="text-sm">
                {t('checkout.cardRegistrationInfo') ||
                  'Serás redirigido a Flow para registrar tu tarjeta de forma segura. Tu información de pago está protegida.'}
              </p>
            </div>

            <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center">
              {t('checkout.confirmationEmail')}{' '}
              <strong className="text-gray-900 dark:text-white break-all">{session.customer_email}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
